/**
 * Runtime contract of the map slot presentation-hints bag
 * (`FmMapSlotProps.presentation`, `shared/config/mapPresentation.ts`).
 *
 * The one validator for every hint, and the one registry of which hints each
 * governed slot renders. A hint the slot does not render is refused by name:
 * a page never carries a hint that is silently dropped.
 */
import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  rejectUnknownKeys,
  validateEnum,
} from './elementFields.validation';
import {
  FM_MAP_BASEMAP_LENSES,
  FM_MAP_BASEMAP_TONES,
  FM_MAP_CATEGORY_PALETTES,
  FM_MAP_FILL_FAMILIES,
  FM_MAP_FIT_TARGETS,
  FM_MAP_PRESENTATION_HINTS,
  FM_MAP_STAGES,
  type FmMapPresentationCapability,
} from '@/shared/config/mapPresentation';
import {
  MEASUREMENT_MAP_SLOT_KEYS,
  REPORT_RANKED_SPATIAL_MAP_SLOT_KEY,
  STAND_MULTI_SELECT_MAP_SLOT_KEY,
  WORKSPACE_PRIMARY_MAP_SLOT_KEY,
} from '@/shared/config/mapSlotRegistry';
import { isOperationColorKey, SPECIES_CODES } from '@/components/ui/charts/palette';

const STAND_MAP_CAPABILITIES: readonly FmMapPresentationCapability[] = [
  'basemap.lens',
  'basemap.tone',
  'basemap.switcher',
  'stage',
  'fitTo:parcel',
  'fitTo:selectable',
  'fitTo:categories',
  'fitTo:selection',
  'reveal',
  'standFill',
  'featureCategories',
  'featureTooltips',
];

/**
 * The workspace map's lens is the owner's basemap choice in the workspace
 * chrome (`baseMapId`), so the bag gives it only a tone and a stand fill.
 */
const WORKSPACE_MAP_CAPABILITIES: readonly FmMapPresentationCapability[] = [
  'basemap.tone',
  'standFill',
];

/** Ranked evidence colours stands by its records, so it takes no fill or categories. */
const RANKED_MAP_CAPABILITIES: readonly FmMapPresentationCapability[] = [
  'basemap.lens',
  'basemap.tone',
  'stage',
  'fitTo:parcel',
  'fitTo:selection',
];

export const FM_MAP_SLOT_PRESENTATION_CAPABILITIES: Readonly<
  Record<string, ReadonlySet<FmMapPresentationCapability>>
> = Object.freeze({
  [STAND_MULTI_SELECT_MAP_SLOT_KEY]: new Set(STAND_MAP_CAPABILITIES),
  [REPORT_RANKED_SPATIAL_MAP_SLOT_KEY]: new Set(RANKED_MAP_CAPABILITIES),
  [WORKSPACE_PRIMARY_MAP_SLOT_KEY]: new Set(WORKSPACE_MAP_CAPABILITIES),
  ...Object.fromEntries(
    MEASUREMENT_MAP_SLOT_KEYS.map((key) => [key, new Set(WORKSPACE_MAP_CAPABILITIES)]),
  ),
});

function refuse(label: string, message: string): never {
  throw new Error(`Invalid descriptor: ${label} ${message}`);
}

function requireCapability(
  capabilities: ReadonlySet<FmMapPresentationCapability>,
  capability: FmMapPresentationCapability,
  slotKey: string,
  label: string,
): void {
  if (!capabilities.has(capability)) {
    refuse(label, `is not rendered by map slot "${slotKey}" (${capability}).`);
  }
}

function validateBasemap(
  value: unknown,
  capabilities: ReadonlySet<FmMapPresentationCapability>,
  slotKey: string,
  label: string,
): void {
  assertRecord(value, label);
  rejectUnknownKeys(value, ['lens', 'tone', 'switcher'], label);
  if (Object.keys(value).length === 0) refuse(label, 'must name a lens, a tone or the switcher.');
  if (value.lens !== undefined) {
    validateEnum(value.lens, FM_MAP_BASEMAP_LENSES, `${label}.lens`);
    requireCapability(capabilities, 'basemap.lens', slotKey, `${label}.lens`);
  }
  if (value.tone !== undefined) {
    validateEnum(value.tone, FM_MAP_BASEMAP_TONES, `${label}.tone`);
    requireCapability(capabilities, 'basemap.tone', slotKey, `${label}.tone`);
  }
  if (value.switcher !== undefined) {
    if (value.switcher !== true) refuse(`${label}.switcher`, 'must be true when present.');
    if (value.lens === undefined) refuse(`${label}.switcher`, 'needs the lens the map opens on.');
    requireCapability(capabilities, 'basemap.switcher', slotKey, `${label}.switcher`);
  }
}

function validateFillKeys(family: string, keys: readonly unknown[], label: string): void {
  keys.forEach((key, index) => {
    const ok =
      family === 'operation'
        ? isOperationColorKey(key)
        : typeof key === 'number' && SPECIES_CODES.includes(key);
    if (!ok) refuse(`${label}[${index}]`, `is not a ${family} colour key (${String(key)}).`);
  });
}

function validateStandFill(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(value, ['family', 'byStandId', 'quietKeys'], label);
  validateEnum(value.family, FM_MAP_FILL_FAMILIES, `${label}.family`);
  assertRecord(value.byStandId, `${label}.byStandId`);
  const entries = Object.entries(value.byStandId);
  if (entries.length === 0) refuse(`${label}.byStandId`, 'must key at least one stand.');
  validateFillKeys(
    String(value.family),
    entries.map(([, key]) => key),
    `${label}.byStandId`,
  );
  if (value.quietKeys !== undefined) {
    assertArray(value.quietKeys, `${label}.quietKeys`);
    validateFillKeys(String(value.family), value.quietKeys, `${label}.quietKeys`);
  }
}

function validateFeatureCategories(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(value, ['palette', 'byFeatureId'], label);
  validateEnum(value.palette, FM_MAP_CATEGORY_PALETTES, `${label}.palette`);
  assertRecord(value.byFeatureId, `${label}.byFeatureId`);
  const entries = Object.entries(value.byFeatureId);
  if (entries.length === 0) refuse(`${label}.byFeatureId`, 'must categorise at least one feature.');
  validateFillKeys(
    'operation',
    entries.map(([, key]) => key),
    `${label}.byFeatureId`,
  );
}

function validateTooltips(value: unknown, label: string): void {
  assertRecord(value, label);
  const entries = Object.entries(value);
  if (entries.length === 0) refuse(label, 'must carry at least one tooltip.');
  entries.forEach(([featureId, text]) => assertNonEmptyString(text, `${label}.${featureId}`));
}

function validateFitTo(
  presentation: Record<string, unknown>,
  capabilities: ReadonlySet<FmMapPresentationCapability>,
  slotKey: string,
  label: string,
): void {
  const target = presentation.fitTo;
  validateEnum(target, FM_MAP_FIT_TARGETS, `${label}.fitTo`);
  requireCapability(
    capabilities,
    `fitTo:${String(target)}` as FmMapPresentationCapability,
    slotKey,
    `${label}.fitTo`,
  );
  if (target === 'categories' && presentation.featureCategories === undefined) {
    refuse(`${label}.fitTo`, '"categories" needs featureCategories to frame.');
  }
}

/**
 * Validate one slot's presentation bag against the hints its slot renders.
 * Cross-field prerequisites that depend on the map element (the selectable
 * set of a multi-select map) are checked by `validateMapPresentationContext`.
 */
export function validateMapPresentation(slotKey: string, value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(value, FM_MAP_PRESENTATION_HINTS, label);
  const capabilities =
    FM_MAP_SLOT_PRESENTATION_CAPABILITIES[slotKey] ?? new Set<FmMapPresentationCapability>();
  if (value.basemap !== undefined)
    validateBasemap(value.basemap, capabilities, slotKey, `${label}.basemap`);
  if (value.stage !== undefined) {
    validateEnum(value.stage, FM_MAP_STAGES, `${label}.stage`);
    requireCapability(capabilities, 'stage', slotKey, `${label}.stage`);
  }
  if (value.fitTo !== undefined) validateFitTo(value, capabilities, slotKey, label);
  if (value.reveal !== undefined) {
    if (value.reveal !== true) refuse(`${label}.reveal`, 'must be true when present.');
    requireCapability(capabilities, 'reveal', slotKey, `${label}.reveal`);
    if (value.standFill === undefined && value.featureCategories === undefined) {
      refuse(`${label}.reveal`, 'needs a standFill or featureCategories to draw in.');
    }
  }
  if (value.standFill !== undefined) {
    requireCapability(capabilities, 'standFill', slotKey, `${label}.standFill`);
    validateStandFill(value.standFill, `${label}.standFill`);
  }
  if (value.featureCategories !== undefined) {
    requireCapability(capabilities, 'featureCategories', slotKey, `${label}.featureCategories`);
    validateFeatureCategories(value.featureCategories, `${label}.featureCategories`);
    if (value.standFill !== undefined) {
      refuse(label, 'carries both standFill and featureCategories; a map has one fill owner.');
    }
  }
  if (value.featureTooltips !== undefined) {
    requireCapability(capabilities, 'featureTooltips', slotKey, `${label}.featureTooltips`);
    validateTooltips(value.featureTooltips, `${label}.featureTooltips`);
  }
}

/**
 * `fitTo: 'selectable'` frames the features the owner may choose, which only a
 * multi-select map element (`FmMapElement.selection`) names.
 */
export function validateMapPresentationContext(
  presentation: unknown,
  hasSelection: boolean,
  label: string,
): void {
  if (!presentation || typeof presentation !== 'object') return;
  if ((presentation as Record<string, unknown>).fitTo === 'selectable' && !hasSelection) {
    refuse(`${label}.fitTo`, '"selectable" needs the map element\'s selection to frame.');
  }
}
